// src/lib/supabaseService.ts
import { supabase } from "./supabaseClient"
import { Transaction, Expense, MonthlyClosure, Store, Gateway, User } from "../types/entites" // adjust path


export const DatabaseService = {
  // === AUTH ===
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({ email: email + '@placeholder.com', password })
    if (error) throw error
    return data
  },

  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    /* Refresh */
    window.location.reload()
  },

  async getCurrentUser() {
    const { data, error } = await supabase.auth.getUser()
    if (error) throw error
    return data.user
  },

  async getUsers(): Promise<Array<{ username: string; id: string; role: string; storeIds: string[] }>> {

    const {data, error} = await supabase.from("users").select("*")
    /* Get also store Ids for each user and return a complete array of user with role, id and storeIds array */
    const usersWithStores = await Promise.all(data!.map(async (user: any) => {
      const stores = await supabase
        .from("ownership")
        .select("store_id")
        .eq("user_id", user.id)
      return { ...user, storeIds: stores.data!.map((s: any) => s.store_id) }
    }))

    if (error) throw error
    return usersWithStores

  },

  async addUser(username: string, password: string, role: string, storeIds: string[]) {
    // Only admin can add users - check should be done before calling this function
    /* Signup user with username and password */
    const { data, error } = await supabase.auth.admin.createUser({
      email: username + '@placeholder.com', // Supabase requires email, we append a domain to username
      password: password,
      email_confirm: true,
      user_metadata: { role }
    })
    if(error) throw error
    const userId = data.user?.id
    if(!userId) throw new Error("User ID not returned after creation")
    /* Create user record in users table */
    const { error: insertError } = await supabase
      .from("users")
      .insert({ id: userId, username, role})
    if (insertError) throw insertError

    /* Link user to stores */
    /* If user admin no need */
    if(role !== 'admin' && storeIds.length > 0) {
      const links = storeIds.map(storeId => ({ user_id: userId, store_id: storeId }))
      const { error: linkError } = await supabase
        .from("ownership")
        .insert(links)
      if (linkError) throw linkError
    }
  },

  async updateUser(id: string, patch: Partial<{ username: string; role: string; storeIds: string[] }>) {
    // Only admin can update users - check should be done before calling this function

    /* Update only the passed data */
    const { username, role, storeIds } = patch

    if(username) {
      /* Update username */
      const { data: existingUser, error: fetchError } = await supabase
        .from("users")
        .select("*")
        .eq("id", id)
        .single()
      if (fetchError) throw fetchError
      if (!existingUser) throw new Error("User not found")
      if (existingUser.username !== username) {
        // Update auth user email (username + domain)
        const { error: authError } = await supabase.auth.admin.updateUserById(id, {
          email: username + '@placeholder.com'
        })
        if (authError) throw authError
        /* Update username in users table */
        const { error: updateError } = await supabase
          .from("users")
          .update({ username })
          .eq("id", id)
        if (updateError) throw updateError
      }
    }

    if(role) {
      /* Update role in users table */
      const { error: updateError } = await supabase
        .from("users")
        .update({ role })
        .eq("id", id)
      if (updateError) throw updateError
    }

    // Update user-store links
    if (storeIds) {
      // Remove old links
      const { error: deleteError } = await supabase
        .from("ownership")
        .delete()
        .eq("user_id", id)
      if (deleteError) throw deleteError

      // Add new links
      const links = storeIds.map(storeId => ({ user_id: id, store_id: storeId }))
      const { error: insertError } = await supabase
        .from("ownership")
        .insert(links)
      if (insertError) throw insertError
    }
  },
  async changePassword(id: string, newPassword: string) {
    // Only admin can change passwords - check should be done before calling this function
    const { error } = await supabase.auth.admin.updateUserById(id, {
      password: newPassword
    })
    if (error) {
      alert("Error changing password: " + error.message)
      throw error
    } 
  },

  async removeUser(id: string) {
    // Only admin can remove users - check should be done before calling this function
    // First, remove from users table (foreign key constraint)
    const { error } = await supabase.from("users").delete().eq("id", id)
    if (error) throw error
    // Then, remove from auth
    const { error: authError } = await supabase.auth.admin.deleteUser(id)
    if (authError) throw authError
  },

  // === USERS ===
  async getUserById(userId: string) {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .single()
    if (error) throw error
    return data
  },

  // === STORES ===
  async getAllStores(): Promise<Store[]> {
    const { data, error } = await supabase
      .from("stores")
      .select("*")
    if (error) throw error
    return data as Store[]
  },

async getStoresByUserId(userId: string): Promise<Store[]> {
  const { data, error } = await supabase
    .from("ownership")
    .select(`
      store:stores (*)
    `)
    .eq("user_id", userId)

  if (error) throw error

  // flatten result → extract stores only
  return data.map((row: any) => row.store) as Store[]
  },

  /* Add store */
  async addStore(name: string, logo_url?: string): Promise<string> {
    const { data, error } = await supabase.from("stores").insert({ name, logo_url }).select().single()
    if (error) throw error
    return data.id
  },

  /* Update store */
  async updateStore(id: string, { name, logo_url }: { name: string; logo_url?: string }): Promise<void> {
    const { error } = await supabase.from("stores").update({ name, logo_url }).eq("id", id)
    if (error) throw error
  },

  /* Remove store */
  async removeStore(id: string): Promise<void> {
    const { error } = await supabase.from("stores").delete().eq("id", id)
    if (error) throw error
  },

  /* GATEWAYS */

  async getGatewaysByStoreId(storeId: string): Promise<Gateway[]> {
    const { data, error } = await supabase
        .from("store_gateways")
        .select(`
        id,
        created_at,
        gateway:gateways (
            id,
            name,
            created_at,
            default
        )
        `)
        .eq("store_id", storeId)

    if (error) throw error

    // flatten nested result so you only return gateway objects
    return data.map((row: any) => row.gateway) as Gateway[]
    },

  async addGateway(name: string, storeId: string): Promise<void> {

    // First, check if gateway already exists (by name)
    let { data, error } = await supabase
      .from("gateways")
      .select("*")
      .eq("name", name)
      .single()

    if(error) {
      /* Add gateway to gateway table */
      const { data , error: insertError } = await supabase
        .from("gateways")
        .upsert({ name })
        .select()

      if (insertError) throw insertError
      console.log("Inserted new gateway  :", data);
      /* Get inserted record id */

      const gatewayData = data?.[0] as Gateway
      console.log("Yeah gatewayData:", gatewayData);
      /* Insert gateway to store_gateways table */
      const { error: linkError } = await supabase
        .from("store_gateways")
        .insert({ store_id: storeId, gateway_id: gatewayData.id })

      if (linkError) throw linkError
    } else {
      const gatewayData = data as Gateway
      console.log("Gateway exists:", gatewayData);
      /* Gateway exists */
      /* Add it to store_gateways table */
      const { error: linkError } = await supabase
        .from("store_gateways")
        .insert({ store_id: storeId, gateway_id: gatewayData.id })
      if (linkError) throw linkError
    }
  },

  async removeGateway(id: string, storeId: string): Promise<void> {
    // Remove from store_gateways first (foreign key constraint)
    let { error } = await supabase
      .from("store_gateways")
      .delete()
      .eq("gateway_id", id)
      .eq("store_id", storeId)
    if (error) throw error
  },

  // === TRANSACTIONS ===
  async getTransactionsByStore(storeId: string): Promise<Transaction[]> {
    const { data, error } = await supabase
      .from("transactions")
      .select("*")
      .eq("store_id", storeId)

    if (error) throw error
    return data as Transaction[]
  },

  async addTransaction(tx: Omit<Transaction, "id">) {
    const { data, error } = await supabase.from("transactions").insert(tx).select().single()
    if (error) throw error
    return data as Transaction
  },

  async updateTransaction(id: string, patch: Partial<Transaction>) {
    const { data, error } = await supabase
      .from("transactions")
      .update(patch)
      .eq("id", id)
      .select()
      .single()
    if (error) throw error
    return data as Transaction
  },

  async deleteTransaction(id: string) {
    const { error } = await supabase.from("transactions").delete().eq("id", id)
    if (error) throw error
    return true
  },

  async getExpensesByStore(storeId: string): Promise<Expense[]> {
    const { data, error } = await supabase
      .from("expenses")
      .select("*")
      .eq("store_id", storeId)

    if (error) throw error
    return data as Expense[]
  },

  async addExpense(exp: Omit<Expense, "id">) {
    const { data, error } = await supabase.from("expenses").insert(exp).select().single()
    if (error) throw error
    return data as Expense
  },

  async updateExpense(id: string, patch: Partial<Expense>) {
    const { data, error } = await supabase
      .from("expenses")
      .update(patch)
      .eq("id", id)
      .select()
      .single()
    if (error) throw error
    return data as Expense
  },

  async deleteExpense(id: string) {
    const { error } = await supabase.from("expenses").delete().eq("id", id)
    if (error) throw error
    return true
  },

  // === CLOSURES ===
  async getMonthlyClosures(userId: string): Promise<MonthlyClosure[]> {
    const { data, error } = await supabase
      .from("monthly_closures")
      .select("*")
      .eq("user_id", userId)

    if (error) throw error
    return data as MonthlyClosure[]
  },

  async getClosuresByStore(storeId: string): Promise<MonthlyClosure[]> {
    const { data, error } = await supabase
      .from("monthly_closures")
      .select("*")
      .eq("store_id", storeId)

    if (error) throw error
    return data as MonthlyClosure[]
  },
}